import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, useWatch } from 'react-hook-form';
import { MdUpdate, MdPersonOff } from 'react-icons/md';
import { FaBan } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Modal, Button } from 'components/common';
import EmployeeSelectField from 'components/common/select/EmployeeSelectField';
import { changeManagerEmployee } from 'redux/organization/employeeSlice';
import { activeEmployeesEnrichedDebounce } from 'helpers/employeeOptionHelper';

/**
 * Reassigns one or more employees to a new manager.
 *
 * Shared by the employee list (bulk, from the selected rows) and the org chart
 * (one person, from the node menu or a drag), so the confirmation people see is
 * the same wherever the change starts.
 *
 * Two things this form used to get wrong, both of which matter now that it is
 * reachable from a chart where a mis-drop is easy:
 *
 *   * it closed on dispatch without waiting, so a rejected change looked like it
 *     had gone through;
 *   * it sent the manager's NAME (activeEmployeesByNameDebounce), which the API
 *     resolved by name. Ids are unambiguous — two people can share a name.
 */
const ChangeManagerForm = ({ open, setOpen, records, onSuccess, directReportsOf }) => {
  const dispatch = useDispatch();
  const { control, reset } = useForm({ defaultValues: { parent_id: null } });
  const managerId = useWatch({ control, name: 'parent_id' });
  const [saving, setSaving] = useState(false);

  const people = records || [];
  const excludedManager = directReportsOf && managerId
    ? people.find((person) => String(person.id) === String(managerId))
    : null;
  const movingPeople = excludedManager
    ? people.filter((person) => String(person.id) !== String(managerId))
    : people;
  const close = () => {
    reset({ parent_id: null });
    setOpen(false);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;

    setSaving(true);
    try {
      // unwrap() so a rejected change throws here instead of being swallowed and
      // reported only by the global error toast — the dialog must stay open.
      await dispatch(changeManagerEmployee({
        employee_ids: movingPeople.map((person) => person.id),
        parent_id: managerId || null,
      })).unwrap();

      close();
      onSuccess?.();
    } catch (error) {
      toast.error(error?.message || 'Could not change the manager.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={open}
      onHide={close}
      title={directReportsOf
        ? `Move direct reports · ${movingPeople.length}`
        : (people.length === 1 ? 'Change manager' : `Change manager · ${people.length} employees`)}
      backdropClick={false}
      footer={(
        <>
          <Button size="sm" type="submit" form="change-manager-form" disabled={saving || movingPeople.length === 0}>
            <MdUpdate /> {saving ? 'Saving…' : 'Change'}
          </Button>
          <Button size="sm" variant="danger" onClick={close} disabled={saving}>
            <FaBan /> Cancel
          </Button>
        </>
      )}
    >
      <form id="change-manager-form" onSubmit={submit}>
        {directReportsOf && (
          <div className="tw-mb-3 tw-rounded-md tw-border tw-border-solid tw-border-blue-200 tw-bg-blue-50 tw-px-3 tw-py-2.5">
            <p className="tw-m-0 tw-text-xs tw-leading-relaxed tw-text-blue-700">
              All direct reports of <strong>{directReportsOf.full_name || directReportsOf.name}</strong> will report directly to the selected manager. The manager of {directReportsOf.name} will not change.
            </p>
          </div>
        )}
        {/* Naming who is affected: from a chart drag it is the only confirmation
            that the right card was picked up. */}
        <div className="tw-mb-3">
          <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-slate-400 tw-mb-1.5">
            Moving
          </p>
          <div className="tw-flex tw-flex-wrap tw-gap-1.5">
            {movingPeople.map((person) => (
              <span
                key={person.id}
                className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-slate-100 tw-text-slate-700 tw-text-xs tw-font-semibold"
              >
                {person.full_name || person.name}
              </span>
            ))}
          </div>
          {excludedManager && (
            <p className="tw-m-0 tw-mt-2 tw-text-xs tw-text-emerald-700">
              <strong>{excludedManager.full_name || excludedManager.name}</strong> is excluded because they are the selected manager.
            </p>
          )}
        </div>

        <div className="tw-mb-2">
          <EmployeeSelectField
            label="New manager"
            name="parent_id"
            control={control}
            loadOptions={activeEmployeesEnrichedDebounce}
            placeholder="Search by name, code or email…"
          />
        </div>

        {/* Clearing the field is a real choice, not an empty form — say so, or it
            reads as "you forgot to pick somebody". */}
        {!managerId && (
          <p className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-amber-700 tw-bg-amber-50 tw-border tw-border-solid tw-border-amber-200 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-m-0">
            <MdPersonOff size={14} className="tw-shrink-0" />
            Leave empty to move {people.length === 1 ? 'this employee' : 'these employees'} to the top level, with no manager.
          </p>
        )}
      </form>
    </Modal>
  );
};

ChangeManagerForm.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func.isRequired,
  records: PropTypes.arrayOf(PropTypes.object),
  onSuccess: PropTypes.func,
  directReportsOf: PropTypes.object,
};

export default ChangeManagerForm;
